"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeMessage = normalizeMessage;
const format_1 = require("../utils/format");
const json_1 = require("../utils/json");
const finish_reason_1 = require("./finish-reason");
const media_1 = require("./message-parts/media");
const text_1 = require("./message-parts/text");
const part_1 = require("./part");
const role_1 = require("./role");
const providers_1 = require("../utils/providers");
/** Normalize the content owned by one already-selected message. */
function normalizeMessageContent(value, nestedContent, parserContext) {
    const rawParts = (() => {
        if (Array.isArray(value.parts)) {
            return value.parts;
        }
        if (Array.isArray(value.content)) {
            return value.content;
        }
        if (Array.isArray(nestedContent?.parts)) {
            return nestedContent.parts;
        }
        return undefined;
    })();
    if (rawParts)
        return (0, part_1.normalizePartList)(rawParts, parserContext);
    if (typeof value.content === "string" && value.content.length > 0) {
        // Koog can serialize an assistant tool-call batch into a string under a
        // tool-role message. Only treat it as parts when every item is a tool.
        const parsedContent = (0, json_1.parseArray)(value.content);
        if (parsedContent?.length) {
            const parsedParts = parsedContent.map((part) => (0, part_1.normalizePartValue)(part, parserContext));
            if (parsedParts.every((parts) => parts.length > 0 &&
                parts.every((part) => part.type === "tool-call" || part.type === "tool-result"))) {
                return parsedParts.flat();
            }
        }
        return (0, media_1.normalizeMediaPartsFromString)(value.content);
    }
    if ((0, json_1.isRecord)(value.content)) {
        return (0, part_1.normalizePartValue)(value.content, parserContext);
    }
    return [];
}
/**
 * Parts and part mutations from fields that live beside `content`. The
 * generic `tool_calls`/`toolCalls` array is universal (OpenAI, LangChain,
 * AI-SDK-flavored logging); everything else folds through
 * `collectSiblingParts`, then message-level citations attach (Chat
 * Completions carries them on the message; Anthropic/Responses carry them
 * per part, handled in `normalize/part.ts`).
 */
function applySiblingFields(value, parts, parserContext) {
    const partContext = {
        normalizePartValue: (part) => (0, part_1.normalizePartValue)(part, parserContext),
        normalizePartList: (values) => (0, part_1.normalizePartList)(values, parserContext),
    };
    const contributions = [];
    for (const provider of (0, providers_1.providersInOrder)(parserContext.preferredProvider)) {
        const siblingParts = provider.collectSiblingParts?.(value, parts, partContext);
        if (siblingParts?.length)
            contributions.push(...siblingParts);
    }
    // One message can expose the same call through native content and one or
    // more framework sibling fields. Claim it in the first carrier only. This
    // is deliberately message-local: identical calls in other turns survive.
    const claimedCarrierByToolCall = new Map();
    const claimKey = (part) => {
        if (part.type !== "tool-call")
            return undefined;
        if (part.toolCallId)
            return `id:${part.toolCallId}`;
        try {
            return `value:${part.toolName}:${JSON.stringify(part.input)}`;
        }
        catch {
            return undefined;
        }
    };
    const claimCarrierParts = (carrier, carrierParts) => carrierParts.filter((part) => {
        const key = claimKey(part);
        if (!key)
            return true;
        const claimedCarrier = claimedCarrierByToolCall.get(key);
        if (claimedCarrier && claimedCarrier !== carrier)
            return false;
        if (!claimedCarrier)
            claimedCarrierByToolCall.set(key, carrier);
        return true;
    });
    for (const part of parts) {
        const key = claimKey(part);
        if (key && !claimedCarrierByToolCall.has(key)) {
            claimedCarrierByToolCall.set(key, "content");
        }
    }
    const bySlot = (slot) => contributions
        .filter((contribution) => contribution.slot === slot)
        .flatMap((contribution) => claimCarrierParts(contribution.sourceKey, contribution.parts));
    parts.unshift(...bySlot("before-content"));
    parts.push(...bySlot("after-content"));
    for (const key of ["tool_calls", "toolCalls"]) {
        parts.push(...claimCarrierParts(key, (0, part_1.normalizePartList)((0, json_1.parseArray)(value[key]) ?? [], parserContext)));
    }
    parts.push(...bySlot("after-tool-calls"));
    const citations = (0, text_1.extractCitations)(value);
    if (citations) {
        const textPart = parts.find((part) => part.type === "text");
        if (textPart) {
            textPart.providerMetadata = { ...textPart.providerMetadata, citations };
        }
    }
}
function normalizeMessage(value, fallbackRole, parserContext) {
    const { source } = parserContext;
    if (typeof value === "string") {
        if (value.length === 0)
            return null;
        for (const provider of (0, providers_1.providersInOrder)(parserContext.preferredProvider)) {
            const preProcessed = provider.tryPreprocessMessage?.(value);
            if (preProcessed?.matched) {
                return preProcessed.value === null
                    ? null
                    : normalizeMessage(preProcessed.value, fallbackRole, parserContext);
            }
        }
        const parts = (0, media_1.normalizeMediaPartsFromString)(value);
        return parts.length > 0 ? { role: fallbackRole, parts, source } : null;
    }
    if (!(0, json_1.isRecord)(value) || (0, format_1.isToolDefinitionMessage)(value))
        return null;
    const envelopeContext = {
        source,
        normalizePartList: (parts) => (0, part_1.normalizePartList)(parts, parserContext),
        normalizeMessage: (nestedValue, nestedFallbackRole) => normalizeMessage(nestedValue, nestedFallbackRole, parserContext),
        isMessageLike: format_1.isMessageLike,
        normalizeFinishReason: finish_reason_1.normalizeFinishReason,
    };
    for (const provider of (0, providers_1.providersInOrder)(parserContext.preferredProvider)) {
        const unwrapped = provider.tryUnwrapMessage?.(value, fallbackRole, envelopeContext);
        if (unwrapped?.matched)
            return unwrapped.value;
    }
    // Standalone tool-call/result values (no message keys): normalize once,
    // inspect the result, rather than shape-probing before normalizing.
    const directParts = !(0, format_1.isMessageLike)(value)
        ? (0, part_1.normalizePartValue)(value, parserContext)
        : [];
    const onlyToolCalls = directParts.length > 0 &&
        directParts.every((part) => part.type === "tool-call");
    const onlyToolResults = directParts.length > 0 &&
        directParts.every((part) => part.type === "tool-result");
    if (onlyToolCalls || onlyToolResults) {
        return {
            role: onlyToolResults ? "tool" : "assistant",
            parts: directParts,
            source,
        };
    }
    const nestedContent = (0, json_1.asRecord)(value.content);
    const recognizedRole = (0, role_1.normalizeRole)(value);
    let role = recognizedRole ??
        (nestedContent ? (0, role_1.normalizeRole)(nestedContent) : undefined) ??
        fallbackRole;
    const parts = normalizeMessageContent(value, nestedContent, parserContext);
    applySiblingFields(value, parts, parserContext);
    role = (0, role_1.coerceRole)(role, value, parts);
    // Last resort for non-message payloads (function-span args/results, plain
    // records, {text} items): represent them instead of dropping — the trace
    // view renders these as JSON too.
    if (parts.length === 0 && !(0, format_1.isMessageLike)(value)) {
        parts.push(...directParts);
    }
    // Choice/candidate-level values are wired in by the accumulator collector.
    const finishReason = (0, finish_reason_1.normalizeFinishReason)(value, (0, json_1.asRecord)(value.response_metadata));
    // Sender identity: an explicit participant name, or — for unrecognized
    // declared roles (e.g. LangGraph putting the agent name in the role field)
    // — the raw role string, so the identity survives the role fallback.
    const declaredRole = (0, json_1.optionalString)(value.role ?? value.author);
    const senderName = (0, json_1.optionalString)(value.name) ??
        (recognizedRole === undefined ? declaredRole : undefined);
    return parts.length > 0
        ? {
            ...((0, json_1.optionalString)(value.id) ? { id: String(value.id) } : {}),
            ...(senderName ? { senderName } : {}),
            role,
            parts,
            ...(finishReason ? { finishReason } : {}),
            source,
        }
        : null;
}
